import { useState, useRef } from "react";
import api from "../../api/client";

/* Import modal for PDS/ADS Excel files. */
export default function PdsAdsImportModal({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // upload | done
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/pds-ads/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStep("done");
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setStep("upload");
    setResult(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1">Import PDS/ADS Records</h2>
        <p className="text-sm text-gray-500 mb-4">
          Upload the standard PDS/ADS Excel export. Recognized columns are matched by header
          name; unrecognized columns are ignored.
        </p>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Upload XLSX File <span className="text-red-500">*</span>
          </label>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            disabled={step !== "upload"}
            onChange={(e) => setFile(e.target.files[0] || null)}
            className="block w-full text-sm disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-1">Only .xlsx files are accepted. Max 25MB.</p>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {result && (
          <div className="bg-gray-50 rounded p-3 mb-4 text-sm">
            <p className="font-medium mb-2">Import Summary</p>
            <p className="text-gray-700">File: {result.filename}</p>
            <p className="text-gray-700">Recognized Columns: {result.recognized_columns}</p>
            <p className="text-green-700">Successfully Imported: {result.inserted}</p>
            <p className="text-yellow-700">Skipped Duplicates (existing ICMS Acct No): {result.skipped_duplicate}</p>
            {result.skipped_blank > 0 && (
              <p className="text-gray-500">Skipped Blank Rows: {result.skipped_blank}</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {step === "upload" ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!file || importing}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {importing ? "Importing…" : "Import"}
              </button>
            </>
          ) : (
            <>
              <button onClick={handleReset} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">
                Import Another
              </button>
              <button onClick={onClose} className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
